// Data Manager Implementation
// ✅ NO MAGIC STRINGS: Use enums/constants for all data keys, events, and error messages

import {
  DataKey,
  DataEvent,
  DataManagerError,
  DataContract,
  DataManagerContract,
} from "./types";

export class DataManager implements DataManagerContract {
  private store: Map<DataKey, unknown> = new Map();
  private eventHandlers: Map<
    DataEvent,
    ((key: DataKey, data?: unknown) => void)[]
  > = new Map();

  get<T>(key: DataKey): T | undefined {
    return this.store.get(key) as T | undefined;
  }

  set<T>(key: DataKey, value: T): void {
    this.store.set(key, value);
    this.emit(DataEvent.UPDATE_SUCCESS, key, value);
  }

  async load<T>(
    key: DataKey,
    loader: () => Promise<T>,
    contract: DataContract<T>,
  ): Promise<T> {
    this.emit(DataEvent.LOAD, key);
    try {
      const raw = await loader();
      const data = contract.transform ? contract.transform(raw) : raw;
      if (!contract.validate(data)) {
        this.emit(
          DataEvent.LOAD_ERROR,
          key,
          DataManagerError.VALIDATION_FAILED,
        );
        throw new Error(DataManagerError.VALIDATION_FAILED);
      }
      this.store.set(key, data);
      this.emit(DataEvent.LOAD_SUCCESS, key, data);
      return data;
    } catch {
      this.emit(DataEvent.LOAD_ERROR, key, DataManagerError.LOAD_FAILED);
      throw new Error(DataManagerError.LOAD_FAILED);
    }
  }

  invalidate(key: DataKey): void {
    this.store.delete(key);
    this.emit(DataEvent.INVALIDATE, key);
  }

  on(event: DataEvent, handler: (key: DataKey, data?: unknown) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  private emit(event: DataEvent, key: DataKey, data?: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((h) => h(key, data));
    }
  }
}
